import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Announcement as AnnouncementIcon,
  PriorityHigh as PriorityHighIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import api from '../utils/axios';

interface Announcement {
  id: string;
  title: string;
  content: string;
  status: 'ACTIVE' | 'ARCHIVED';
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  startDate: string;
  endDate?: string;
  createdAt: string;
  creator: {
    firstName: string;
    lastName: string;
  };
}

interface AnnouncementsWidgetProps {
  isWidget?: boolean;
}

const AnnouncementsWidget: React.FC<AnnouncementsWidgetProps> = ({ isWidget = false }) => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        setLoading(true);
        const response = await api.get('/announcements/public');
        if (response.data.success) {
          setAnnouncements(response.data.data);
        } else {
          setError('Failed to fetch announcements');
        }
      } catch (error) {
        console.error('Error fetching announcements:', error);
        setError('Failed to fetch announcements');
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, []);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return 'error';
      case 'HIGH':
        return 'warning';
      case 'NORMAL':
        return 'primary';
      case 'LOW':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityIcon = (priority: string) => {
    if (priority === 'URGENT' || priority === 'HIGH') {
      return <PriorityHighIcon />;
    }
    return null;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (announcements.length === 0) {
    return (
      <Box textAlign="center" p={3}>
        <AnnouncementIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography color="text.secondary">
          No active announcements at the moment.
        </Typography>
      </Box>
    );
  }

  if (isWidget) {
    return (
      <Box sx={{ height: '100%', overflow: 'auto' }}>
        <List>
          {announcements.map((announcement, index) => (
            <React.Fragment key={announcement.id}>
              <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
                <Box sx={{ width: '100%' }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                      {announcement.title}
                    </Typography>
                    <Chip
                      label={announcement.priority}
                      color={getPriorityColor(announcement.priority) as any}
                      icon={getPriorityIcon(announcement.priority)}
                      size="small"
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      mb: 1
                    }}
                  >
                    {announcement.content}
                  </Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      By {announcement.creator.firstName} {announcement.creator.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(announcement.startDate)}
                    </Typography>
                  </Box>
                </Box>
              </ListItem>
              {index < announcements.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Public Announcements
        </Typography>
        <List>
          {announcements.map((announcement, index) => (
            <React.Fragment key={announcement.id}>
              <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
                <Box sx={{ width: '100%' }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                      {announcement.title}
                    </Typography>
                    <Chip
                      label={announcement.priority}
                      color={getPriorityColor(announcement.priority) as any}
                      icon={getPriorityIcon(announcement.priority)}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" paragraph>
                    {announcement.content}
                  </Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      By {announcement.creator.firstName} {announcement.creator.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(announcement.startDate)}
                    </Typography>
                  </Box>
                </Box>
              </ListItem>
              {index < announcements.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default AnnouncementsWidget; 